import { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, Ref, useEffect, useState } from 'react';
import classNames from 'classnames';

import { CalendarIcon, CloseIcon } from '../../../icons';

import styles from './Trigger.module.css';

/** Откуда пришёл коммит текста: явное завершение ввода (Enter) или просто потеря фокуса —
 *  потерять фокус можно и кликнув по стрелке/дропдауну внутри уже открытого календаря. */
export type CommitTextSource = 'blur' | 'enter';

export type TriggerProps = {
  displayText: string;
  placeholder: string;
  isDisabled: boolean;
  isClearable: boolean;
  /** Десктоп — можно печатать дату вручную; мобайл — только выбор в шторке (без нативной клавиатуры). */
  allowTyping: boolean;
  className?: string;
  onOpen: () => void;
  onIconClick: () => void;
  onClear: (event: MouseEvent) => void;
  onCommitText: (text: string, source: CommitTextSource) => void;
  ref?: Ref<HTMLDivElement>;
};

const Trigger = ({
  displayText,
  placeholder,
  isDisabled,
  isClearable,
  allowTyping,
  className,
  onOpen,
  onIconClick,
  onClear,
  onCommitText,
  ref,
}: TriggerProps) => {
  const [inputValue, setInputValue] = useState(displayText);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setInputValue(displayText);
    }
  }, [displayText, isEditing]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (allowTyping) {
      setIsEditing(true);
    } else {
      event.target.blur();
    }

    onOpen();
  };

  const handleBlur = () => {
    setIsEditing(false);
    onCommitText(inputValue, 'blur');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onCommitText(inputValue, 'enter');
    }
  };

  return (
    <div
      ref={ref}
      className={classNames(styles.trigger, className, { [styles.triggerDisabled]: isDisabled })}>
      <CalendarIcon
        className={styles.triggerIcon}
        onClick={isDisabled ? undefined : onIconClick}
      />
      <input
        type="text"
        disabled={isDisabled}
        readOnly={!allowTyping}
        value={inputValue}
        placeholder={placeholder}
        className={styles.triggerInput}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {isClearable && Boolean(inputValue) && (
        <CloseIcon
          className={styles.clearIcon}
          onClick={onClear}
        />
      )}
    </div>
  );
};

export default Trigger;
